import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DisplayName } from '../account-teaching-details/DisplayName';
import { TeacherPageContext, ViewMode } from './TeacherPageContextProvider';
import { PHOTO_TYPE, PhotoTypes, TeacherProfileVO } from './types';
import TeacherProfileDonateBar from './TeacherProfileDonateBar';
import { TeacherShareBar } from '../teacherprofile-react/TeacherShareBar';
import { getTeacherProfileTwitterText } from '../sharebar/sharebarHelperFunctions';
import { Svg } from '../common/Svg';
import { LightBoxWrapper } from '../common/LightBoxWrapper';
import { Sticky } from '../utils/Sticky';
import { buildHackySchoolPageUrl } from '../common/buildHackySchoolPageUrl';
import { ToggleViewModeButton } from './ToggleViewModeButton';
import { BirthdayBanner } from './BirthdayBanner';

interface TeacherProfileBannerProps {
    teacherProfileVO: TeacherProfileVO,
    setUploadModalPhotoType: (type: PHOTO_TYPE) => void,
}

const EDIT_DETAILS_URL = `${secureBaseURL}teacher/edit_teaching_details.html`;

export const TeacherProfileBanner = ({
    teacherProfileVO,
    setUploadModalPhotoType,
}: TeacherProfileBannerProps) => {
    const teacherDetails = teacherProfileVO.teacherDetails;
    const schoolDetails = teacherProfileVO.schoolDetails;
    const teacherPageContext = useContext(TeacherPageContext);
    const [editMode, setEditMode] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [shouldCloseNameLightbox, setShouldCloseNameLightbox] = useState(false);
    const [isGiveBarOpen, setIsGiveBarOpen] = useState(false);
    // Which content is showing inside the same sheet (give widget vs share bar).
    // Mobile only — on desktop the give widget is always inline and the share
    // bar lives at the top of the banner.
    const [sheetContent, setSheetContent] = useState<'give' | 'share'>('give');
    const [isStickyHeaderVisible, setIsStickyHeaderVisible] = useState(false);
    const donateWrapperRef = useRef<HTMLDivElement>(null);
    const donateWasInViewRef = useRef(false);
    const selfShareBarRef = useRef<HTMLDivElement>(null);
    const inlineGiveRef = useRef<HTMLButtonElement>(null);
    const [isInlineGiveVisible, setIsInlineGiveVisible] = useState(true);
    const [prefix, setPrefix] = useState(teacherDetails.prefix || '');
    const [displayName, setDisplayName] = useState(teacherDetails.displayName || '');
    const [draftPrefix, setDraftPrefix] = useState(teacherDetails.prefix || '');
    const [draftDisplayName, setDraftDisplayName] = useState(teacherDetails.displayName || '');
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [formSuccessMessages, setFormSuccessMessages] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!teacherPageContext.showDonorFeatures) return;
        const node = donateWrapperRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                donateWasInViewRef.current = true;
                setIsStickyHeaderVisible(false);
            } else if (donateWasInViewRef.current) {
                setIsStickyHeaderVisible(true);
            }
        }, { root: null, threshold: 0 });
        observer.observe(node);
        return () => observer.disconnect();
    }, [teacherPageContext.showDonorFeatures]);

    // Sort of, but not quite, like Sticky.tsx. Toggle visibility-class on div X whenever we scroll div Y out of frame.
    useEffect(() => {
        const node = inlineGiveRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(([entry]) => {
            setIsInlineGiveVisible(entry.isIntersecting);
        }, { root: null, threshold: 0 });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const renderPhotoEditButton = (photoType: PHOTO_TYPE) => {
        return (teacherPageContext.isOwner && teacherPageContext.viewMode === ViewMode.TEACHER &&
            <button type="button"
                onClick={() => setUploadModalPhotoType(photoType)}
                className="teacher-profile-edit-button teacher-profile-edit-photo js-classroom-photo-upload js-admin"
                aria-label={'Edit Profile Photo'}>
                <Svg icon="write" iconFamily="junior"/>
            </button>
        );
    };

    const handleDraftInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        if (name === 'prefix') setDraftPrefix(value);
        if (name === 'displayName') setDraftDisplayName(value);
    }, []);

    const handleDraftPrefixChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setDraftPrefix(event.target.value);
    };

    const openNameEditor = () => {
        setDraftPrefix(prefix);
        setDraftDisplayName(displayName);
        setFormErrors({});
        setFormSuccessMessages({});
        setIsEditingName(true);
    };

    const handleSaveName = () => {
        setFormErrors({});
        setFormSuccessMessages({});

        const prefixAjaxCall = $.ajax(EDIT_DETAILS_URL, {
            type: 'POST',
            data: { prefix: draftPrefix },
            xhrFields: { withCredentials: true },
        });

        const displayNameAjaxCall = $.ajax(EDIT_DETAILS_URL, {
            type: 'POST',
            data: { displayName: draftDisplayName },
            xhrFields: { withCredentials: true },
        });

        Promise.all([prefixAjaxCall, displayNameAjaxCall])
            .then(() => {
                setPrefix(draftPrefix);
                setFormSuccessMessages({ displayName: 'Display Name saved!' });
                setDisplayName(draftDisplayName);
                setTimeout(() => {
                    setShouldCloseNameLightbox(true);
                }, 2000);
            })
            .catch((data: JQuery.jqXHR) => {
                try {
                    setFormErrors(JSON.parse(data.responseText));
                } catch {
                    setFormErrors({ displayName: 'Error updating display name, please try again.' });
                }
            });
    };

    const handleCancelName = () => {
        setFormErrors({});
        setShouldCloseNameLightbox(true);
    };

    const renderProfilePhoto = () => {
        return (<div className={'teacher-photo js-teacher-photo-holder'}>
            {renderPhotoEditButton(PhotoTypes.PROFILE)}
            <img src={teacherDetails.profilePhotoUrl} alt=""/>
        </div>);
    };
    const renderProfileInfo = () => {
        const address = schoolDetails.address;
        const city = address.city ? address.city + ', ' + address.state : '';
        const school = schoolDetails.name ? schoolDetails.name : '';

        const isOwnerTeacherView = teacherPageContext.viewMode === ViewMode.TEACHER && teacherPageContext.isOwner;
        return (<div className="teacher-classroom-info">
            <div className="classroom-name">
                {(isOwnerTeacherView ? prefix : teacherDetails.prefix) + ' ' + (isOwnerTeacherView ? displayName : teacherDetails.displayName)}
            </div>
            {isOwnerTeacherView && (
                <button type="button"
                    className="teacher-profile-edit-button teacher-profile-edit-name"
                    aria-label="Edit display name"
                    onClick={openNameEditor}>
                    <span>Edit display name</span>
                </button>
            )}
            <div className="classroom-location">
                <div className="school">
                    {schoolDetails.schoolId
                        ? <a className="subtle-link" href={buildHackySchoolPageUrl(schoolDetails.schoolId)}>{school}</a>
                        : school}
                </div>
                <div className="city">
                    {address.zip
                        ? <a className="subtle-link" href={`${baseURL}donors/map.html?startingZip=${address.zip}`}>{city}</a>
                        : city}
                </div>
                {schoolDetails.isEquityFocused &&
                    <div className="equity-focus">
                        <span className="tooltip-wrapper">
                            Historically underfunded school
                            <Svg icon="help" additionalClasses="populate-tooltip"/>
                            <span className="hover-tooltip">
                                At historically underfunded schools, students have limited access to resources
                                and learning materials. <a href={`${baseURL}student-focus`}>Learn more about our student focus.</a>
                            </span>
                        </span>
                    </div>
                }
            </div>
        </div>);
    };

    const renderDonationCard = () => {
        const isTeacherOwnView = teacherPageContext.viewMode === ViewMode.TEACHER;
        const openSheet = (type: 'give' | 'share') => {
            setSheetContent(type);
            setIsGiveBarOpen(true);
        };
        return (
            <>
                <div className={`teacher-give-overlay ${isGiveBarOpen ? 'is-open' : ''}`}
                    onClick={() => setIsGiveBarOpen(false)}
                    aria-hidden="true"
                />
                <div ref={donateWrapperRef}
                    className={`teacher-donation-wrapper ${isGiveBarOpen ? 'is-open' : ''} ${isInlineGiveVisible ? 'inline-give-visible' : ''} sheet-content-${sheetContent}`}>
                    {isTeacherOwnView
                        ? (
                            <div className="teacher-mobile-action-peek" aria-hidden={isGiveBarOpen}>
                                <button type="button" className="button-secondary action-peek-button"
                                    onClick={() => openSheet('give')}
                                    aria-label="Open donation form"
                                    tabIndex={isGiveBarOpen ? -1 : 0}>
                                    Give
                                </button>
                                <button type="button" className="button-primary action-peek-button"
                                    onClick={() => openSheet('share')}
                                    aria-label="Open share options"
                                    tabIndex={isGiveBarOpen ? -1 : 0}>
                                    Share
                                </button>
                            </div>
                        )
                        : (
                            // Only visible when .inline-give-visible absent from parent
                            <div className="give-bar-peek-slot" aria-hidden={isGiveBarOpen}>
                                <button type="button" className="give-bar-peek button-primary"
                                    onClick={() => openSheet('give')}
                                    aria-label="Open donation form"
                                    tabIndex={isGiveBarOpen ? -1 : 0}>
                                    Give to this classroom
                                </button>
                            </div>
                        )}
                    <button type="button" className="give-bar-close"
                        onClick={() => setIsGiveBarOpen(false)}
                        aria-hidden={!isGiveBarOpen}
                        aria-label="Close"
                        tabIndex={isGiveBarOpen ? 0 : -1}>
                        <Svg icon="delete" attributes={{ role: 'presentation' }}/>
                    </button>

                    {/* Give widget — always rendered. On mobile, CSS hides it unless
                        sheet-content-give is on the wrapper. On desktop it's always shown. */}
                    <div className="sheet-content sheet-content-give-pane">
                        <TeacherProfileDonateBar teacherDetails={teacherDetails}/>
                    </div>

                    {/* Share bar — only relevant for teachers viewing their own page.
                        Mobile-only inside the sheet (the desktop pill at the top of the
                        banner handles desktop). */}
                    {isTeacherOwnView && (
                        <div className="sheet-content sheet-content-share-pane">
                            <div className="teacher-share-tools teacher-self-share-bar in-sheet" style={{ display: 'block' }}>
                                <TeacherShareBar
                                    teacherProfileURL={teacherProfileVO.teacherProfileUrl}
                                    pageName={pageName}
                                    tafURL={`${secureBaseURL}donors/share_teacher_profile.html?teacher=${teacherDetails.id}`}
                                    getTafEmail={window.getTafEmail}
                                    getTrackingLink={window.getTrackingLink}
                                    facebookAppId={facebookAppId}
                                    getTeacherProfileTwitterText={getTeacherProfileTwitterText}
                                    schoolName={schoolDetails.name}
                                    root={null}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const renderEditButton = () => {
        return (
            <button onClick={() => {
                setEditMode(!editMode);
            } }>
                Edit profile
            </button>
        );
    };

    const renderToggleViewButton = () => {
        return (<button onClick={teacherPageContext.toggleViewMode}>
            Switch to {teacherPageContext.viewMode === ViewMode.TEACHER ? 'Donor' : 'Teacher'} view
        </button>
        );
    };

    const renderTeacherActionsView = () => {
        return (
            teacherPageContext.viewMode === ViewMode.TEACHER &&
                (<div className="teacher-actions-view">
                    <h2>Quick Actions</h2>
                    {renderEditButton()}
                    {renderToggleViewButton()}
                </div>
                )
        );
    };

    const renderDonorActionsView = () => {
        return (
            teacherPageContext.viewMode === ViewMode.DONOR &&
            (<div className="donor-actions-wrapper">
                {teacherPageContext.isOwner ? renderToggleViewButton() : null}
            </div>)
        );
    };

    const renderTeacherStickyBar = () => {
        const stickyHeaderCity = schoolDetails.address.city
            ? `${schoolDetails.address.city}, ${schoolDetails.address.state}`
            : '';

        return (<div className="teacher-sticky-header"
            aria-hidden={!isStickyHeaderVisible}
            style={{ transform: isStickyHeaderVisible ? 'translateY(0)' : 'translateY(-100%)' }}>
            <div className="container">
                {teacherDetails.profilePhotoUrl && (
                    <img className="teacher-sticky-header-photo"
                        src={teacherDetails.profilePhotoUrl} alt=""/>
                )}
                <div className="teacher-sticky-header-info">
                    <span className="teacher-sticky-header-name">
                        {teacherDetails.prefix} {teacherDetails.displayName}
                    </span>
                    <span className="teacher-sticky-header-school">
                        {schoolDetails.name}
                        {stickyHeaderCity && ` · ${stickyHeaderCity}`}
                    </span>
                </div>
                <button type="button"
                    className="button button-primary small teacher-sticky-header-give"
                    onClick={() => setIsGiveBarOpen(true)}
                    tabIndex={isStickyHeaderVisible ? 0 : -1}>
                    Give to this classroom
                </button>
            </div>
        </div>);
    };

    const renderTeacherHeroSection = () => {
        return (<div className="teacher-hero-section">
            {teacherPageContext.viewMode === ViewMode.TEACHER && (
                <>
                    <Sticky>
                        <div
                            className="teacher-share-tools teacher-self-share-bar"
                            style={{ display: 'block' }}>
                            <TeacherShareBar
                                teacherProfileURL={teacherProfileVO.teacherProfileUrl}
                                pageName={pageName}
                                tafURL={`${secureBaseURL}donors/share_teacher_profile.html?teacher=${teacherDetails.id}`}
                                getTafEmail={window.getTafEmail}
                                getTrackingLink={window.getTrackingLink}
                                facebookAppId={facebookAppId}
                                getTeacherProfileTwitterText={getTeacherProfileTwitterText}
                                schoolName={schoolDetails.name}
                                root={selfShareBarRef.current}
                            />
                        </div>
                    </Sticky>
                </>
            )}
            <ToggleViewModeButton />
            <div className="container">
                <div className="teacher-hero-info">
                    <div className="mobile">
                        <h1 className="thick">Donate to {teacherDetails.possessiveName}{'\u00A0'}classroom</h1>
                        <p className="subheader">Every donation goes towards supplies this classroom needs</p>
                        <button ref={inlineGiveRef}
                            type="button"
                            className="inline-give-button button-primary"
                            onClick={() => {
                                setSheetContent('give');
                                setIsGiveBarOpen(true);
                            }}
                            aria-label="Open donation form">
                            Give to this classroom
                        </button>
                    </div>
                    {teacherDetails.profilePhotoUrl && renderProfilePhoto()}
                    {renderProfileInfo()}
                </div>
                {renderDonationCard()}
                <div className="hide-for-now">
                    {renderDonorActionsView()}
                    {renderTeacherActionsView()}
                </div>
            </div>
        </div>);
    };

    const renderEditDisplayName = () => {
        return <LightBoxWrapper
            fixed={true}
            closeLightbox={shouldCloseNameLightbox}
            unMountHandler={() => {
                setShouldCloseNameLightbox(false);
                setIsEditingName(false);
            }}>
            <div className="teacher-profile-edit-lightbox">
                <DisplayName
                    prefix={draftPrefix}
                    displayName={draftDisplayName}
                    formErrors={formErrors}
                    formSuccessMessages={formSuccessMessages}
                    handleInputChange={handleDraftInputChange}
                    handlePrefixChange={handleDraftPrefixChange}
                    handleDisplayNameChange={() => {
                    }}
                    showSpanishHonorifics={schoolDetails.address.state?.endsWith('PR')}
                />
                <div className="teacher-profile-edit-actions">
                    <button type="button"
                        className="teacher-profile-edit-save button-primary small"
                        onClick={handleSaveName}>
                        Save
                    </button>
                    <button type="button"
                        className="teacher-profile-edit-cancel button-secondary small"
                        onClick={handleCancelName}>
                        Cancel
                    </button>
                </div>
            </div>
        </LightBoxWrapper>;
    };

    return (
        <>
            <BirthdayBanner teacherProfileVO={teacherProfileVO} />
            {teacherPageContext.showDonorFeatures && !teacherPageContext.isOwner && renderTeacherStickyBar()}
            {renderTeacherHeroSection()}
            {isEditingName && renderEditDisplayName()}
        </>
    );
};
